import React, { useEffect, useState } from "react";
import SelectField from "../../../../../../global components/Forms/SelectField/SelectField";
import s from "./Table.module.scss";
import SearchInput from "../../../../../../global components/Forms/SearchInput/SearchInput";
import ReusableTable from "../../../../../../global components/Table/Table";
import NoData from "../../../../../../global components/Table/NoData/NoData";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import { PaginatedSentMessage } from "../../../../../../libs/Utils/Types/Message";
import useSWR from "swr";
import {
  parseClientError,
  swrFetcher,
} from "../../../../../../libs/api/Endpoints";
import dayjs from "dayjs";

import Envelope from "../../../../../../global components/Icons/Envelope/Envelope";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import Unauthorised from "../../../../../../global components/Unauthorised";

function Table() {
  const token = UseAuthToken();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage /*setCurrentPage*/] = useState(1);
  const [pageItems /*setPageItems*/] = useState(10);
  const [messageUnauthorized, setMessageUnauthorized] = useState<
    boolean | { title: string; message: string }
  >(false);
  const [filter, setFilter] = useState<string>("");
  const navigate = useNavigate();
  const {
    data: TableContacts,
    isLoading,
    error,
  } = useSWR<PaginatedSentMessage, any, any>(
    token
      ? [
          "api/admin/overview/recent-messages",
          token,
          {
            page: currentPage,
            limit: pageItems,
            search: searchValue,
            ...(filter && { status: filter }),
          },
        ]
      : null,
    swrFetcher,
    {
      revalidateOnFocus: true, // Refetch when the window regains focus
      // Refetch every 10 seconds (10000 ms)
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );

  // Define table columns
  const columns = [
    {
      title: "Message title",
      dataIndex: "messageTitle",
      key: "messageTitle",
      render: (messageTitle: string) => (
        <span className={clsx(s.cell, s.messageTitle)}>{messageTitle}</span>
      ),
    },
    {
      title: "Message type",
      dataIndex: "mesageType",
      key: "mesageType",
      render: (mesageType: string) => (
        <span className={clsx(s.cell)}>{mesageType.toUpperCase()}</span>
      ),
    },
    {
      title: "Sender",
      dataIndex: "sender",
      key: "sender",
      render: (sender: string) => (
        <span className={clsx(s.cell)}>{sender}</span>
      ),
    },
    {
      title: "Groups",
      dataIndex: "group",
      key: "group",
      render: (group: string) => <span className={clsx(s.cell)}>{group}</span>,
    },
    {
      title: "Contacts",
      dataIndex: "contacts",
      key: "contacts",
      render: (contacts: string) => (
        <span className={clsx(s.cell)}>{contacts}</span>
      ),
    },

    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => (
        <span className={s.cell}>
          {" "}
          {dayjs.utc(date).local().format("MMM D, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span className={clsx(s.status, s.statusCell, s[status.toLowerCase()])}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
    {
      title: "Open rate",
      dataIndex: "openRate",
      key: "openRate",
      render: (openRate: string) => (
        <span className={clsx(s.cell)}>{openRate}</span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (text: any, record: { key: string }) => (
        <h5
          className={s.view}
          onClick={() =>
            navigate(`/dashboard/Message/message-details?msgId=${record.key}`)
          }
        >
          View
        </h5>
      ),
    },
  ];

  // Transform API response to table data
  const transformedData = TableContacts?.data.map((contact) => ({
    key: contact.id,
    messageTitle: contact.title || "N/A",
    mesageType: contact.type.replace("_", " ") || "N/A",
    group: contact.noOfContactGroups || "0",
    contacts: contact.totalRecipients || "0",
    creditUsed: contact.totalCreditsUsed || "0",
    date: contact.sentAt || "N/A",
    status: contact.status || "N/A",
    sender: contact.user
      ? (contact.user?.firstName || "") + " " + (contact.user?.lastName || "")
      : "N/A",
    openRate: contact.deliveryRate ? contact.deliveryRate.toLocaleString() : 0,
    actions: "", // You can customize actions here as needed
  }));

  function handleFilter(value: string) {
    setFilter(value);
  }
  const handleSearch = (value: string) => {
    setSearchValue(value);
    // You can add your search logic here, e.g., fetching data based on the search term
  };
  useEffect(() => {
    if (error) {
      const e = parseClientError(error);

      if (e.statusCode === 403) {
        setMessageUnauthorized(true);
      } else {
        setMessageUnauthorized({
          title: "Error!",
          message:
            e.message.charAt(0).toUpperCase() + e.message.slice(1) ||
            "Something went wrong",
        });
      }
    }
  }, [error]);
  return (
    <div className={s.wrapper}>
      <div className={s.tableHeader}>
        <h3 className={s.subtitle}>Recent messages</h3>
        <h5 className={s.viewA} onClick={() => navigate("/dashboard/Message")}>
          View all
        </h5>
      </div>

      <div className={s.tableWrapper}>
        <div className={s.formWrapper}>
          <div className={s.searchInput}>
            <SearchInput
              style={{ width: "100%", height: "100%" }}
              placeholder="search"
              width={332}
              height={45}
              handleSearch={handleSearch}
            />
          </div>
          <SelectField
            bordered
            label={"Status:"}
            defaultValue={"all"}
            wrapperClassname={s.SelectWrapper}
            className={clsx(s.selectInput)}
            onChange={(value: any) => handleFilter(value)}
            style={{ width: "100%", height: "100%" }}
            options={[
              { value: "all", label: "All" }, // Option to reset the filter
              { value: "sent", label: "Sent" },
              { value: "pending", label: "Pending" },
              { value: "failed", label: "Failed" },
              { value: "scheduled", label: "Scheduled" },
            ]}
          />
        </div>
        {/* <Paginate
        onChange={handlePageChange}
        total={TableContacts?.totalDocs || 0}
        pageSize={pageItems}
      /> */}
        <ReusableTable
          loading={isLoading}
          noDataComponent={
            messageUnauthorized ? (
              typeof messageUnauthorized == "boolean" ? (
                <Unauthorised />
              ) : (
                <Unauthorised error={messageUnauthorized} />
              )
            ) : (
              <NoData
                Message="No message yet"
                subMessage="Get started by sending a message"
                icon={<Envelope fill="#F0EFEE" height={60} width={80} />}
              />
            )
          }
          dataSource={transformedData || []}
          columns={columns}
          //rowSelection={rowSelection} // Include row selection prop
        />
      </div>
    </div>
  );
}
export default Table;
